// WalletDialog.js - COMPLETE WITH VALIDATION

import React, { useCallback, useMemo, useState, memo, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Alert,
  AlertTitle,
  Divider,
  Chip,
  Stack,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Avatar,
  Paper,
  Autocomplete,
  useMediaQuery,
  Grid,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Save as SaveIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { WALLET_TYPES, CRYPTO_CURRENCIES, COUNTRIES_TAX_CONFIG, SUPPORTED_COUNTRIES, BANK_PAYOUT_COUNTRIES } from './constants';
import StripeFCButton from '../../../../Components/payments/StripeFCButton';
import StripePlatformFCButton from '../../../../Components/payments/StripePlatformFCButton';
import ManualBankEntryForm from '../../../../Components/payments/ManualBankEntryForm';
import WiseFlow from '../../../../Components/payments/wise/WiseFlow';
import StripeConnectButton, { StripeVerifiedBadge } from '../../../../Components/StripeConnectButton';
import PayPalConnectButton, { PayPalVerifiedBadge } from '../../../../Components/PayPalConnectButton';
import {
  formatRoutingNumber,
  formatAccountNumber,
  formatSWIFT,
  formatIBAN,
  validateRoutingNumber,
  validateAccountNumber,
  isMaskedValue,
  getRoutingHelperText,
  getAccountHelperText
} from './../../../../Components/payments/taxWalletInputValid';

// Country filter for bank-country Autocomplete — matches label, ISO code,
// or currency so users can search "Brazil", "BR", or "BRL" interchangeably.
const bankCountryFilterOptions = createFilterOptions({
  stringify: (option) => `${option.label} ${option.value} ${option.currency}`,
  trim: true,
});

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED PREVIEW CARD - OUTSIDE MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const WalletPreviewCard = memo(({ 
  walletData,
  selectedWalletType,
  taxRequired,
  taxSufficient,
  isFormValid,
  completionPercentage,
  existingPayPalWallet,
  existingStripeWallet
}) => {
  const theme = useTheme();
  
  // Get wallet icon and color
  const walletIcon = selectedWalletType?.icon || <PaymentIcon />;
  const walletColor = useMemo(() => {
    switch (walletData.wallet_type) {
      case 'paypal': return '#0070ba';
      case 'stripe': return '#635bff';
      case 'wise': return '#9fe870';
      case 'bank_ach': return theme.palette.success.main;
      case 'crypto': return '#f7931a';
      default: return theme.palette.primary.main;
    }
  }, [walletData.wallet_type, theme]);

  // Mask sensitive information
  const maskAccountNumber = (number) => {
    if (!number) return '';
    const cleaned = number.toString().replace(/\D/g, '');
    if (cleaned.length <= 4) return cleaned;
    return '●●●●' + cleaned.slice(-4);
  };

  return (
    <Card
      elevation={3}
      sx={{
        overflow: 'hidden',
        position: 'sticky',
        top: 16,
      }}
    >
      {/* Header with Wallet Type Icon */}
      <Box
        sx={{
          height: 140,
          background: walletData.wallet_type 
            ? `linear-gradient(135deg, ${walletColor} 0%, ${alpha(walletColor, 0.7)} 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            fontSize: '2.5rem',
          }}
        >
          {walletIcon}
        </Avatar>
      </Box>

      <CardContent sx={{ pb: 3 }}>
        <Stack spacing={2.5}>
          {/* Wallet Type */}
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {selectedWalletType?.label || 'Payment Method'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedWalletType?.description || 'Select a payment method type'}
            </Typography>
          </Box>

          <Divider />

          {/* Nickname */}
          {walletData.nickname && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Nickname
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {walletData.nickname}
              </Typography>
            </Box>
          )}

          {/* Wallet-Specific Details */}
          {walletData.wallet_type === 'paypal' && (
            <>
              {existingPayPalWallet ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  <Typography variant="caption">
                    PayPal Already Connected
                  </Typography>
                </Alert>
              ) : walletData.paypal_email && isValidEmail(walletData.paypal_email) ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    PayPal Email
                  </Typography>
                  <Typography variant="body2">
                    {walletData.paypal_email}
                  </Typography>
                </Box>
              ) : null}
            </>
          )}

          {walletData.wallet_type === 'stripe' && (
            <>
              {existingStripeWallet ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  <Typography variant="caption">
                    Stripe Already Connected
                  </Typography>
                </Alert>
              ) : walletData.stripe_account_id ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Stripe Account
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontFamily="monospace">
                      {maskAccountNumber(walletData.stripe_account_id)}
                    </Typography>
                    {walletData.verification_status === 'verified' && (
                      <Chip 
                        label="Verified"
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                      />
                    )}
                  </Stack>
                </Box>
              ) : null}
            </>
          )}

          {walletData.wallet_type === 'wise' && walletData.wise_email && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Wise Account
              </Typography>
              <Typography variant="body2">{walletData.wise_email}</Typography>
              {walletData.wise_currency && (
                <Chip 
                  label={walletData.wise_currency}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          )}

          {walletData.wallet_type === 'bank_ach' && (
            <>
              {walletData.bank_name && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Bank Name
                  </Typography>
                  <Typography variant="body2">{walletData.bank_name}</Typography>
                </Box>
              )}
              {walletData.bank_account_number && !isMaskedValue(walletData.bank_account_number) && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Account Number
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {maskAccountNumber(walletData.bank_account_number)}
                  </Typography>
                  {walletData.bank_account_type && (
                    <Chip 
                      label={walletData.bank_account_type}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              )}
            </>
          )}

          {walletData.wallet_type === 'crypto' && (
            <>
              {walletData.crypto_currency && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Cryptocurrency
                  </Typography>
                  <Chip 
                    label={walletData.crypto_currency}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
              {walletData.crypto_wallet_address && !isMaskedValue(walletData.crypto_wallet_address) && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Wallet Address
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontFamily="monospace"
                    sx={{ 
                      wordBreak: 'break-all',
                      fontSize: '0.75rem'
                    }}
                  >
                    {walletData.crypto_wallet_address.slice(0, 6)}...{walletData.crypto_wallet_address.slice(-6)}
                  </Typography>
                  {walletData.crypto_network && (
                    <Chip 
                      label={walletData.crypto_network}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              )}
            </>
          )}

          {/* Tax Compliance Status */}
          {taxRequired && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Tax Compliance
              </Typography>
              <Chip 
                icon={taxSufficient ? <CheckCircleIcon /> : <WarningIcon />}
                label={taxSufficient ? "Tax Info Complete" : "Tax Info Required"}
                size="small"
                color={taxSufficient ? "success" : "warning"}
                variant="outlined"
              />
            </Box>
          )}

          {/* Completion Progress */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Setup Progress
              </Typography>
              <Typography 
                variant="caption" 
                fontWeight={600} 
                color={isFormValid ? "success.main" : "primary"}
              >
                {completionPercentage}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              color={isFormValid ? "success" : "primary"}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            />
          </Box>

          {/* Status Alert */}
          {isFormValid ? (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="caption">
                ✓ Ready to save
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="caption">
                Complete all required fields
              </Typography>
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props changed
  return (
    JSON.stringify(prevProps.walletData) === JSON.stringify(nextProps.walletData) &&
    prevProps.selectedWalletType?.value === nextProps.selectedWalletType?.value &&
    prevProps.taxRequired === nextProps.taxRequired &&
    prevProps.taxSufficient === nextProps.taxSufficient &&
    prevProps.isFormValid === nextProps.isFormValid &&
    prevProps.completionPercentage === nextProps.completionPercentage &&
    prevProps.existingPayPalWallet?.id === nextProps.existingPayPalWallet?.id &&
    prevProps.existingStripeWallet?.id === nextProps.existingStripeWallet?.id
  );
});

// ═══════════════════════════════════════════════════════════════════
// EMAIL VALIDATION HELPER
// ═══════════════════════════════════════════════════════════════════
const isValidEmail = (email) => {
  if (!email) return false;
  const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return pattern.test(email);
};

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED FORM CONTENT - OUTSIDE MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const WalletFormContent = memo(({
  walletData,
  mode,
  selectedWalletType,
  taxRequired,
  taxSufficient,
  taxMessage,
  taxInfo,
  showTaxWarning,
  handleInputChange,
  handleSelectChange,
  handleOpenTaxDialog,
  handlePlaidSuccess,
  existingPayPalWallet,
  existingStripeWallet,
  isCreator,
  isMobile,
  theme,
  routingValidation,
  accountValidation,
  onLinkBankSuccess,
  onLinkBankError,
}) => {
  // Country selected for bank entry. Defaults to the creator's tax
  // country (if known) so US creators don't have to change it, and
  // international creators see their country pre-filled. Kept in local
  // state because the field isn't stored on the wallet record yet —
  // backend wiring lives in InternationalBankEntryForm.
  const [bankCountry, setBankCountry] = useState(taxInfo?.country || 'US');

  useEffect(() => {
    if (taxInfo?.country && taxInfo.country !== bankCountry) {
      setBankCountry(taxInfo.country);
    }
    // Only re-sync when the tax country itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxInfo?.country]);

  const bankCountryOption = useMemo(
    () =>
      BANK_PAYOUT_COUNTRIES.find((c) => c.value === bankCountry) ||
      BANK_PAYOUT_COUNTRIES.find((c) => c.value === 'US') ||
      BANK_PAYOUT_COUNTRIES[0],
    [bankCountry]
  );

  const isUsBankCountry = bankCountry === 'US';

  // Memoize wallet type selection dropdown
  const walletTypeSelect = useMemo(() => (
    <FormControl fullWidth margin="normal" required>
      <InputLabel>Payment Method Type</InputLabel>
      <Select
        value={walletData.wallet_type || ''}
        onChange={handleSelectChange("wallet_type")}
        label="Payment Method Type"
        sx={{ borderRadius: 2 }}
      >
        {WALLET_TYPES.map((type) => (
          <MenuItem key={type.value} value={type.value}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}>
              <Box sx={{ fontSize: '1.5rem' }}>{type.icon}</Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight={600}>{type.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {type.description}
                </Typography>
              </Box>
              {type.value !== 'paypal' && (
                <Chip 
                  label="Tax Info Required" 
                  size="small" 
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ), [walletData.wallet_type, handleSelectChange]);

  // Memoize tax requirement alert
  const taxRequirementAlert = useMemo(() => {
    if (!selectedWalletType || !taxRequired) return null;

    if (!taxSufficient) {
      return (
        <Alert 
          severity={showTaxWarning ? "error" : "warning"} 
          sx={{ mb: 2 }}
          icon={<WarningIcon />}
          action={
            handleOpenTaxDialog ? (
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleOpenTaxDialog}
                variant="outlined"
              >
                Complete Tax Info
              </Button>
            ) : null
          }
        >
          <AlertTitle>Tax Information Required</AlertTitle>
          {taxMessage || `Tax information is required for ${selectedWalletType.label} payments.`}
          {!taxInfo && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              You must complete tax information before adding this payment method.
            </Typography>
          )}
        </Alert>
      );
    }

    return (
      <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
        <AlertTitle>Tax Compliance Complete</AlertTitle>
        {taxMessage || `Tax information for ${selectedWalletType.label} has been completed.`}
      </Alert>
    );
  }, [selectedWalletType, taxRequired, taxSufficient, taxMessage, taxInfo, showTaxWarning, handleOpenTaxDialog]);

  // Memoize wallet-specific fields
  const walletFields = useMemo(() => {
    switch (walletData.wallet_type) {
      case "paypal":
        return (
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {existingPayPalWallet ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                <AlertTitle>PayPal Already Connected</AlertTitle>
                <Typography variant="body2">
                  You already have a PayPal account connected
                  {existingPayPalWallet.paypal_email && ` (${existingPayPalWallet.paypal_email})`}.
                  You can manage it from your payment methods list.
                </Typography>
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Secure PayPal Verification</AlertTitle>
                  <Typography variant="body2">
                    Connect your PayPal account securely to receive payments.
                    We'll never see your PayPal password.
                  </Typography>
                </Alert>

                <PayPalConnectButton
                  isCreator={isCreator}
                  fullWidth
                  onError={(err) => console.error('PayPal connection error:', err)}
                  redirectPath={isCreator ? "/creatorconnect/dashboard?tab=wallets" : "/dashboard?tab=account"}
                />
              </>
            )}
          </Paper>
        );

      case "wise":
        return (
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wise Email"
                  type="email"
                  value={walletData.wise_email || ''}
                  onChange={handleInputChange("wise_email")}
                  required
                  margin="normal"
                  placeholder="your.email@example.com"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={walletData.wise_currency || ''}
                    onChange={handleSelectChange("wise_currency")}
                    label="Currency"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="USD">USD - US Dollar</MenuItem>
                    <MenuItem value="EUR">EUR - Euro</MenuItem>
                    <MenuItem value="GBP">GBP - British Pound</MenuItem>
                    <MenuItem value="CAD">CAD - Canadian Dollar</MenuItem>
                    <MenuItem value="AUD">AUD - Australian Dollar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        );

      case "bank_ach": {

        return (
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack spacing={2.5} sx={{ py: 1 }}>
              <Stack spacing={2} alignItems="center">
                <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Link your bank account to receive payouts. Choose one of the
                  options below.
                </Typography>
              </Stack>

              {/* Option 1: Link via Bank Login (US-only, Stripe Financial Connections).
                  Hidden for non-US countries because Stripe FC can't link them. */}
              {isUsBankCountry && (
                <>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Option 1: Link via Bank Login
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Securely log in to your bank — fastest and most secure option.
                    </Typography>
                    {isCreator ? (
                      <StripePlatformFCButton
                        onSuccess={onLinkBankSuccess}
                        onError={onLinkBankError}
                        variant="contained"
                        color="primary"
                        label={isMobile ? 'Link Bank' : 'Link via Bank Login'}
                        size={isMobile ? 'small' : 'medium'}
                      />
                    ) : (
                      <StripeFCButton
                        onSuccess={onLinkBankSuccess}
                        onError={onLinkBankError}
                        variant="contained"
                        color="primary"
                        label={isMobile ? 'Link Bank' : 'Link via Bank Login'}
                        size={isMobile ? 'small' : 'medium'}
                      />
                    )}
                  </Box>

                  <Divider>
                    <Chip label="OR" size="small" />
                  </Divider>
                </>
              )}

              {/* Option 2: Enter bank details manually. Always rendered.
                  The country selector stays visible for every country — only
                  the form below it swaps between the US ACH flow and the
                  international preview form. */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {isUsBankCountry
                    ? 'Option 2: Enter Bank Details Manually'
                    : 'Enter Bank Details Manually'}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1.5 }}
                >
                  Select your country — we'll show the right fields for it.
                </Typography>

                <Autocomplete
                  fullWidth
                  disableClearable
                  autoHighlight
                  options={BANK_PAYOUT_COUNTRIES}
                  value={bankCountryOption}
                  onChange={(_, option) => {
                    if (option?.value) setBankCountry(option.value);
                  }}
                  getOptionLabel={(option) => option ? `${option.flag} ${option.label}` : ''}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  filterOptions={bankCountryFilterOptions}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <Box
                        component="li"
                        key={option.value}
                        {...rest}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography sx={{ fontSize: '1.25rem', lineHeight: 1 }}>
                          {option.flag}
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {option.label}
                        </Typography>
                        <Chip
                          label={option.currency}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bank Country"
                      size="small"
                      placeholder="Search by country, code, or currency"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                      }}
                    />
                  )}
                />

                {isUsBankCountry ? (
                  <ManualBankEntryForm
                    onSuccess={onLinkBankSuccess}
                    onError={onLinkBankError}
                  />
                ) : (
                  <WiseFlow
                    country={bankCountry}
                    isCreator={isCreator}
                    onSuccess={onLinkBankSuccess}
                    onError={onLinkBankError}
                  />
                )}
              </Box>
            </Stack>
          </Paper>
        );
      }

      case "crypto":
        return (
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Cryptocurrency</InputLabel>
                  <Select
                    value={walletData.crypto_currency || ''}
                    onChange={handleSelectChange("crypto_currency")}
                    label="Cryptocurrency"
                    sx={{ borderRadius: 2 }}
                  >
                    {CRYPTO_CURRENCIES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Network</InputLabel>
                  <Select
                    value={walletData.crypto_network || ''}
                    onChange={handleSelectChange("crypto_network")}
                    label="Network"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="mainnet">Mainnet</MenuItem>
                    <MenuItem value="polygon">Polygon</MenuItem>
                    <MenuItem value="bsc">Binance Smart Chain</MenuItem>
                    <MenuItem value="arbitrum">Arbitrum</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wallet Address"
                  value={walletData.crypto_wallet_address || ''}
                  onChange={handleInputChange("crypto_wallet_address")}
                  required
                  margin="normal"
                  multiline
                  rows={2}
                  inputProps={{ maxLength: 64 }}
                  error={walletData.crypto_wallet_address && isMaskedValue(walletData.crypto_wallet_address)}
                  helperText={
                    isMaskedValue(walletData.crypto_wallet_address)
                      ? 'Please re-enter your wallet address'
                      : walletData.crypto_wallet_address
                      ? `${walletData.crypto_wallet_address.length} characters`
                      : 'Enter your cryptocurrency wallet address'
                  }
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Paper>
        );

      case "stripe":
        return (
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {existingStripeWallet ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                <AlertTitle>Stripe Already Connected</AlertTitle>
                <Typography variant="body2">
                  You already have a Stripe account connected.
                  You can manage it from your payment methods list.
                </Typography>
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Secure Verification</AlertTitle>
                  <Typography variant="body2">
                    Connect your Stripe account securely to receive payments.
                    We'll never see your Stripe password.
                  </Typography>
                </Alert>

                {walletData.stripe_account_id && walletData.verification_status === 'verified' ? (
                  <StripeVerifiedBadge
                    accountId={walletData.stripe_account_id}
                    status={walletData.stripe_status}
                  />
                ) : (
                  <StripeConnectButton
                    isCreator={isCreator}
                    fullWidth
                    onError={(err) => console.error('Stripe connection error:', err)}
                    redirectPath={isCreator ? "/creatorconnect/dashboard" : "/dashboard?tab=account"}
                  />
                )}
              </>
            )}
          </Paper>
        );

      default:
        return (
          <Alert severity="info">
            <Typography variant="body2">
              Select a payment method type to continue
            </Typography>
          </Alert>
        );
    }
  }, [walletData, handleInputChange, handleSelectChange, handlePlaidSuccess, existingPayPalWallet, existingStripeWallet, isCreator, isMobile, routingValidation, accountValidation, onLinkBankSuccess, onLinkBankError, bankCountry, bankCountryOption, isUsBankCountry]);

  return (
    <>
      {/* Security Notice for Edit Mode */}
      {mode === 'edit' && walletData._edit_mode && walletData._sensitive_fields?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <AlertTitle>Security Notice</AlertTitle>
          <Typography variant="body2">
            For security reasons, you must re-enter the following sensitive information:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {walletData._sensitive_fields.map((field, idx) => (
              <li key={idx}>
                <Typography variant="body2">
                  {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {/* Basic Information Section */}
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 2 : 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <DescriptionIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Basic Information
          </Typography>
        </Stack>

        <TextField
          fullWidth
          label="Nickname (Optional)"
          value={walletData.nickname || ''}
          onChange={handleInputChange("nickname")}
          margin="normal"
          placeholder="e.g., My Primary PayPal"
          helperText="Give this payment method a memorable name"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        
        {walletTypeSelect}
      </Paper>

      {/* Tax Requirement Alert */}
      {taxRequirementAlert}

      {/* Wallet Configuration Section */}
      {selectedWalletType && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 2.5,
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              mb: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <SecurityIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedWalletType.label} Configuration
              </Typography>
            </Stack>
            
            {walletFields}
          </Paper>
        </>
      )}
    </>
  );
});

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const WalletDialog = ({
  open,
  mode,
  wallet,
  walletData,
  onClose,
  onSubmit,
  onChange,
  // Tax integration props
  taxInfo = null,
  onOpenTaxDialog = null,
  isTaxInfoRequired = () => false,
  isTaxInfoSufficient = () => true,
  getTaxRequirementMessage = () => null,
  // User type
  isCreator = true,
  // Callbacks
  onSuccess = null,
  // Existing wallets
  existingPayPalWallet = null,
  existingStripeWallet = null,
  // Bank linking callbacks
  onLinkBankSuccess = null,
  onLinkBankError = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showTaxWarning, setShowTaxWarning] = useState(false);

  // Clear masked values when dialog opens in edit mode
  useEffect(() => {
    if (open && walletData._edit_mode) {
      const sensitiveFields = walletData._sensitive_fields || [];
      sensitiveFields.forEach(field => {
        if (walletData[field] && isMaskedValue(walletData[field])) {
          onChange(field, '');
        }
      });
    }
  }, [open, walletData._edit_mode, walletData._sensitive_fields, onChange]);

  // Memoize wallet type selection
  const selectedWalletType = useMemo(() => 
    WALLET_TYPES.find((t) => t.value === walletData.wallet_type), 
    [walletData.wallet_type]
  );

  // Check tax requirements
  const taxRequired = useMemo(() => {
    if (!walletData.wallet_type) return false;
    return isTaxInfoRequired(walletData.wallet_type);
  }, [walletData.wallet_type, isTaxInfoRequired]);

  const taxSufficient = useMemo(() => {
    if (!walletData.wallet_type) return true;
    return isTaxInfoSufficient(walletData.wallet_type);
  }, [walletData.wallet_type, isTaxInfoSufficient]);

  const taxMessage = useMemo(() => {
    if (!walletData.wallet_type) return null;
    return getTaxRequirementMessage(walletData.wallet_type);
  }, [walletData.wallet_type, getTaxRequirementMessage]);

  // Validate routing number
  const routingValidation = useMemo(() => {
    if (!walletData.bank_routing_number) {
      return { isValid: false, helperText: getRoutingHelperText(false, '') };
    }
    
    if (isMaskedValue(walletData.bank_routing_number)) {
      return { isValid: false, helperText: 'Please re-enter routing number' };
    }
    
    const isValid = validateRoutingNumber(walletData.bank_routing_number);
    return {
      isValid,
      helperText: getRoutingHelperText(isValid, walletData.bank_routing_number)
    };
  }, [walletData.bank_routing_number]);

  // Validate account number
  const accountValidation = useMemo(() => {
    if (!walletData.bank_account_number) {
      return { isValid: false, helperText: getAccountHelperText(false, '') };
    }
    
    if (isMaskedValue(walletData.bank_account_number)) {
      return { isValid: false, helperText: 'Please re-enter account number' };
    }
    
    const isValid = validateAccountNumber(walletData.bank_account_number);
    return {
      isValid,
      helperText: getAccountHelperText(isValid, walletData.bank_account_number)
    };
  }, [walletData.bank_account_number]);

  // Form validation with enhanced checks
  const isFormValid = useMemo(() => {
    if (!walletData.wallet_type) return false;

    switch (walletData.wallet_type) {
      case 'paypal':
        return existingPayPalWallet || (walletData.paypal_email && isValidEmail(walletData.paypal_email));
      case 'stripe':
        return existingStripeWallet || (walletData.stripe_account_id && walletData.verification_status === 'verified');
      case 'wise':
        return walletData.wise_email && walletData.wise_currency;
      case 'bank_ach':
        // Bank ACH uses self-contained components (StripePlatformFCButton /
        // ManualBankEntryForm) that submit directly via onLinkBankSuccess —
        // the dialog's Save button is hidden for this type.
        return true;
      case 'crypto':
        if (!walletData.crypto_currency || !walletData.crypto_wallet_address || !walletData.crypto_network) return false;
        if (isMaskedValue(walletData.crypto_wallet_address)) return false;
        return true;
      default:
        return false;
    }
  }, [walletData, existingPayPalWallet, existingStripeWallet, routingValidation, accountValidation]);

  // Calculate completion percentage
  const completionPercentage = useMemo(() => {
    if (!walletData.wallet_type) return 0;
    
    let requiredFields = ['wallet_type'];
    let filledFields = [walletData.wallet_type].filter(Boolean);

    switch (walletData.wallet_type) {
      case 'paypal':
        requiredFields.push('paypal_email');
        if (walletData.paypal_email && isValidEmail(walletData.paypal_email)) {
          filledFields.push('paypal_email');
        }
        break;
      case 'stripe':
        requiredFields.push('stripe_account_id', 'verification_status');
        if (walletData.stripe_account_id) filledFields.push('stripe_account_id');
        if (walletData.verification_status === 'verified') filledFields.push('verification_status');
        break;
      case 'wise':
        requiredFields.push('wise_email', 'wise_currency');
        if (walletData.wise_email) filledFields.push('wise_email');
        if (walletData.wise_currency) filledFields.push('wise_currency');
        break;
      case 'bank_ach':
        // Bank ACH uses self-contained components — always show 100%
        requiredFields.push('wallet_type');
        filledFields.push('wallet_type');
        break;
      case 'crypto':
        requiredFields.push('crypto_currency', 'crypto_wallet_address', 'crypto_network');
        if (walletData.crypto_currency) filledFields.push('crypto_currency');
        if (walletData.crypto_wallet_address && !isMaskedValue(walletData.crypto_wallet_address)) filledFields.push('crypto_wallet_address');
        if (walletData.crypto_network) filledFields.push('crypto_network');
        break;
    }

    return Math.round((filledFields.length / requiredFields.length) * 100);
  }, [walletData]);

  // Stable event handlers with formatting
  const handleInputChange = useCallback((field) => (e) => {
    let value = e.target.value;
    
    // Format bank routing number
    if (field === 'bank_routing_number') {
      value = formatRoutingNumber(value);
    }
    
    // Format bank account number
    if (field === 'bank_account_number') {
      value = formatAccountNumber(value);
    }
    
    // Format SWIFT code
    if (field === 'swift_code') {
      value = formatSWIFT(value);
    }
    
    // Format IBAN
    if (field === 'iban') {
      value = formatIBAN(value);
    }
    
    // Prevent editing masked values
    if (isMaskedValue(value)) {
      return;
    }
    
    onChange(field, value);
    
    if (field === 'wallet_type') {
      setShowTaxWarning(false);
    }
  }, [onChange]);

  const handleSelectChange = useCallback((field) => (e) => {
    onChange(field, e.target.value);
    
    if (field === 'wallet_type') {
      setShowTaxWarning(false);
    }
  }, [onChange]);

  // Enhanced submit handler with tax validation
  const handleSubmit = useCallback(() => {
    if (taxRequired && !taxSufficient) {
      setShowTaxWarning(true);
      return;
    }
    
    onSubmit();
  }, [taxRequired, taxSufficient, onSubmit]);

  const handleOpenTaxDialog = useCallback(() => {
    if (onOpenTaxDialog) {
      onOpenTaxDialog();
    }
  }, [onOpenTaxDialog]);

  const handlePlaidSuccess = useCallback((walletData) => {
    console.log('✅ Plaid bank account linked successfully:', walletData);
    onClose();
    if (onSuccess) {
      onSuccess(walletData);
    }
  }, [onClose, onSuccess]);

  // Memoize form content props
  const formContentProps = useMemo(() => ({
    walletData,
    mode,
    selectedWalletType,
    taxRequired,
    taxSufficient,
    taxMessage,
    taxInfo,
    showTaxWarning,
    handleInputChange,
    handleSelectChange,
    handleOpenTaxDialog,
    handlePlaidSuccess,
    existingPayPalWallet,
    existingStripeWallet,
    isCreator,
    isMobile,
    theme,
    routingValidation,
    accountValidation,
    onLinkBankSuccess,
    onLinkBankError,
  }), [
    walletData,
    mode,
    selectedWalletType,
    taxRequired,
    taxSufficient,
    taxMessage,
    taxInfo,
    showTaxWarning,
    handleInputChange,
    handleSelectChange,
    handleOpenTaxDialog,
    handlePlaidSuccess,
    existingPayPalWallet,
    existingStripeWallet,
    isCreator,
    isMobile,
    theme,
    routingValidation,
    accountValidation,
    onLinkBankSuccess,
    onLinkBankError,
  ]);

  if (!open) return null;

  // Mobile View
  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen
        PaperProps={{
          elevation: 0,
          sx: { 
            borderRadius: 0,
            boxShadow: 'none',
            m: 0,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            height: '100vh',
            maxHeight: '100vh',
          }
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: theme.shadows[4],
            pt: 'max(16px, env(safe-area-inset-top, 0px))',
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.secondary.main, 0.3)} 0%, transparent 70%)`,
              pointerEvents: 'none'
            }}
          />
          
          <Box sx={{ position: 'relative', zIndex: 1, p: 2, pb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                  {mode === "create" ? "Add Payment Method" : "Edit Payment Method"}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Secure payment processing
                </Typography>
              </Box>
              
              <IconButton 
                onClick={onClose}
                size="small"
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>

            <LinearProgress 
              variant="determinate" 
              value={completionPercentage} 
              sx={{
                height: 4,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'white',
                  borderRadius: 3,
                }
              }}
            />
          </Box>
        </Box>

        <DialogContent 
          sx={{ 
            p: 2,
            bgcolor: 'background.default',
            overflowY: 'auto',
            flex: 1,
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <WalletFormContent {...formContentProps} />
        </DialogContent>

        <Divider />

        <DialogActions 
          sx={{ 
            p: 2, 
            gap: 1, 
            bgcolor: 'background.paper',
            paddingBottom: 'calc(98px + 16px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <Button 
            onClick={onClose} 
            color="inherit"
            size="medium"
            sx={{ borderRadius: 2, fontSize: '0.875rem' }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {walletData.wallet_type !== 'stripe' && walletData.wallet_type !== 'bank_ach' && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isFormValid}
              startIcon={<SaveIcon />}
              size="medium"
              sx={{ borderRadius: 2, fontSize: '0.875rem' }}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  // Desktop View - Full Screen with Two Columns
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          m: 0,
          maxHeight: '100vh',
          maxWidth: '100vw',
          borderRadius: 0,
        }
      }}
      disablePortal={false}
      keepMounted={false}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'stretch',
        }
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          height: 80,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <PaymentIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {mode === "create" ? "Add Payment Method" : "Edit Payment Method"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Secure payment processing and verification
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              width: 200,
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
            {completionPercentage}%
          </Typography>
          <IconButton 
            onClick={onClose}
            sx={{
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Main Content - Two Column Layout */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Column - Preview */}
        <Box
          sx={{
            width: 380,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            overflowY: 'auto',
            p: 3,
          }}
        >
          <WalletPreviewCard
            walletData={walletData}
            selectedWalletType={selectedWalletType}
            taxRequired={taxRequired}
            taxSufficient={taxSufficient}
            isFormValid={isFormValid}
            completionPercentage={completionPercentage}
            existingPayPalWallet={existingPayPalWallet}
            existingStripeWallet={existingStripeWallet}
          />
        </Box>

        {/* Right Column - Form */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
          <WalletFormContent {...formContentProps} />

          {/* Action Buttons - Sticky at bottom */}
          <Box 
            sx={{ 
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.default',
              pt: 3,
              pb: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              mt: 4,
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button 
                onClick={onClose} 
                color="inherit"
                size="large"
                sx={{ borderRadius: 2, px: 3 }}
              >
                Cancel
              </Button>
              {walletData.wallet_type !== 'stripe' && walletData.wallet_type !== 'bank_ach' && (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={!isFormValid}
                  startIcon={<SaveIcon />}
                  size="large"
                  sx={{ borderRadius: 2, px: 4 }}
                >
                  {mode === "create" ? "Add Payment Method" : "Update Payment Method"}
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default React.memo(WalletDialog);